import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer, PageToolbar, ToolbarDivider } from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import { PageToolbarSkeleton, CardSkeleton } from '../../components/ui/Skeleton';
import { getOrgId, organisationsAPI, getCurrentUser } from '../../services/api';
import toast from '../../services/toast';
import { Loader2, ArrowLeft, Link2, Unlink, Clock } from 'lucide-react';
import CommitHeatmap from '../../components/CommitHeatmap/CommitHeatmap';
import MemberChurnCardColumn from './MemberChurnCardColumn';
import MemberStreamsProjectsTreeSection from './MemberStreamsProjectsTreeSection';
import MemberTechStackSection from './MemberTechStackSection';
import MemberSkillsSection from './MemberSkillsSection';
import OrgMemberAvatar from '../../components/ui/OrgMemberAvatar';
import {
  profileHeaderClass,
  profileNameClass,
  profileContactClass,
} from '../../components/members/memberViewTheme';
import SelectDropdown from '../../components/ui/SelectDropdown';
import { FeatureGate, useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import AskVantiChip from '../../components/ui/AskVantiChip';

const MemberDetail = () => {
  const { confirm } = useConfirm();
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [techCategoriesOpen, setTechCategoriesOpen] = useState(() => new Set());
  const [unlinking, setUnlinking] = useState(false);
  const [streamsExpanded, setStreamsExpanded] = useState(() => new Set());
  const [projectsExpanded, setProjectsExpanded] = useState(() => new Set());
  const [churnData, setChurnData] = useState<any>(null);
  const [churnLoading, setChurnLoading] = useState(false);
  const [churnPeriod, setChurnPeriod] = useState('yearly');
  const [churnChartType, setChurnChartType] = useState('area');
  const [roles, setRoles] = useState<any[]>([]);
  const [roleUpdating, setRoleUpdating] = useState(false);
  const [activeMemberTab, setActiveMemberTab] = useState('activity');
  const skillsTabEnabled = useFeatureFlag('ui.memberDetail.skillsTab', false);
  const isOwnProfile = useMemo(() => {
    try {
      return getCurrentUser()?.id === memberId;
    } catch {
      return false;
    }
  }, [memberId]);

  const memberTabs = useMemo(
    () =>
      [
        { id: 'activity', label: 'Activity' },
        { id: 'churn', label: 'Churn & Output' },
        { id: 'assignments', label: 'Assignments' },
        skillsTabEnabled ? { id: 'skills', label: 'Skills' } : null,
      ].filter((t): t is { id: string; label: string } => t != null),
    [skillsTabEnabled]
  );

  const onMemberTabListKeyDown = useCallback(
    (e) => {
      const ids = memberTabs.map((t) => t.id);
      const idx = ids.indexOf(activeMemberTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveMemberTab(ids[(idx + 1) % ids.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveMemberTab(ids[(idx - 1 + ids.length) % ids.length]);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveMemberTab(ids[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveMemberTab(ids[ids.length - 1]);
      }
    },
    [memberTabs, activeMemberTab]
  );

  const churnWindowDays = churnPeriod === 'weekly' ? 7 : churnPeriod === 'yearly' ? 365 : 30;

  useEffect(() => {
    if (orgId && detail) {
      organisationsAPI.getRoles(orgId).then((list) => setRoles(Array.isArray(list) ? list : []));
    }
  }, [orgId, detail]);

  const refetchMemberDetail = useCallback(async () => {
    if (!orgId || !memberId) return;
    try {
      const data = await organisationsAPI.getMemberDetail(orgId, memberId);
      setDetail(data || null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to load member');
      setDetail(null);
    }
  }, [orgId, memberId]);

  useEffect(() => {
    let cancelled = false;
    getOrgId()
      .then((id) => {
        if (!cancelled) setOrgId(id);
        return id;
      })
      .then((id) => {
        if (id && memberId) return organisationsAPI.getMemberDetail(id, memberId);
        return null;
      })
      .then((data) => {
        if (!cancelled) setDetail(data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error((err as Error).message || 'Failed to load member');
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
    if (!detail?.streams?.length && !detail?.projects?.length) {
      setStreamsExpanded(new Set());
      setProjectsExpanded(new Set());
      return;
    }
    const streamIds = [
      ...new Set(
        [
          ...(detail.streams || []).map((s) => s.id),
          ...(detail.projects || []).map((p) => p.streamId),
        ].filter(Boolean)
      ),
    ];
    setStreamsExpanded(streamIds.length > 0 ? new Set([streamIds[0]]) : new Set());
    setProjectsExpanded(new Set());
  }, [memberId, detail?.streams, detail?.projects]);

  useEffect(() => {
    if (!orgId || !memberId) return;
    let cancelled = false;
    setChurnLoading(true);
    organisationsAPI
      .getMemberChurn(orgId, memberId, churnWindowDays)
      .then((data) => {
        if (!cancelled) setChurnData(data ?? null);
      })
      .catch(() => {
        if (!cancelled) setChurnData(null);
      })
      .finally(() => {
        if (!cancelled) setChurnLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, memberId, churnWindowDays]);

  const toggleStream = (streamId) => {
    setStreamsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(streamId)) next.delete(streamId);
      else next.add(streamId);
      return next;
    });
  };
  const toggleProject = (projectId) => {
    setProjectsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const handleUnlinkOne = async (githubId, githubUsername) => {
    if (!orgId || !memberId || githubId == null) return;
    const login = (githubUsername || '').trim() || 'this account';
    const ok = await confirm({
      title: 'Unlink GitHub account',
      message: `Are you sure you want to unlink @${login} from this member? This will remove the contributor association.`,
      confirmLabel: 'Unlink',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    setUnlinking(true);
    try {
      await organisationsAPI.unlinkMemberGitHub(orgId, memberId, githubId);
      toast.success('GitHub link removed');
      await refetchMemberDetail();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to unlink');
    } finally {
      setUnlinking(false);
    }
  };

  const handleRoleChange = async (newRoleId) => {
    if (!orgId || !memberId) return;
    setRoleUpdating(true);
    try {
      await organisationsAPI.setMemberRole(orgId, memberId, newRoleId || null);
      const role = roles.find((r) => r.id === newRoleId) || null;
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              member: {
                ...prev.member,
                roleId: newRoleId || null,
                role: role ? { id: role.id, name: role.name } : null,
              },
            }
          : null
      );
      toast.success('Role updated');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update role');
    } finally {
      setRoleUpdating(false);
    }
  };

  if (loading) {
    return (
      <PageContainer className="dashboard-org-tight">
        <PageToolbarSkeleton />
        <div className="mb-6">
          <CardSkeleton />
        </div>
        <div className="flex gap-4 border-b border-dark-border pb-3 mb-6">
          <div className="h-8 w-24 rounded bg-dark-elevated animate-pulse" />
          <div className="h-8 w-32 rounded bg-dark-elevated animate-pulse" />
          <div className="h-8 w-28 rounded bg-dark-elevated animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  if (!orgId || !detail) {
    return (
      <PageContainer>
        <PageToolbar title="Member">
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="btn-icon-mdl"
            aria-label="Back to Members"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </PageToolbar>
        <p className="text-slate-400">Member not found.</p>
      </PageContainer>
    );
  }

  const { member, streams, projects, repositories, unassignedRepositories, techStacks } = detail;
  const toggleTechCategory = (cat) => {
    setTechCategoriesOpen((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const memberDisplayName = member.username || member.email || 'Member';

  return (
    <PageContainer className="dashboard-org-tight">
      <PageToolbar title={memberDisplayName}>
        <button
          type="button"
          onClick={() => navigate('/members')}
          className="btn-icon-mdl"
          aria-label="Back to Members"
          title="Back to Members"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </PageToolbar>
      <ToolbarDivider />

      <div className={`${profileHeaderClass} mb-6`}>
        <div className="flex flex-wrap items-start gap-4">
          <OrgMemberAvatar member={member} size="lg" />
          <div className="min-w-0 flex-1">
            <p className={profileNameClass}>{memberDisplayName}</p>
            {member.email && <p className={`${profileContactClass} truncate`}>{member.email}</p>}
            <p className={`${profileContactClass} mt-1`}>
              <Clock className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
              {member.lastLoginAt
                ? `Last login: ${new Date(member.lastLoginAt).toLocaleString()}`
                : 'Last login: never'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {Array.isArray(member.linkedContributors) &&
                member.linkedContributors.map((link) => (
                  <span
                    key={link.githubId}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-vivid-green-cyan/15 text-vivid-green-cyan px-2.5 py-1 text-sm font-medium"
                  >
                    <Link2 className="w-4 h-4 flex-shrink-0" />@{link.githubUsername}
                    <button
                      type="button"
                      onClick={() => handleUnlinkOne(link.githubId, link.githubUsername)}
                      disabled={unlinking}
                      className="ml-0.5 rounded p-0.5 text-vivid-green-cyan hover:bg-vivid-green-cyan/20 disabled:opacity-50"
                      aria-label={`Unlink ${link.githubUsername}`}
                    >
                      <Unlink className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-auto">
            <div className="flex items-center gap-2">
              <label htmlFor="member-role-select" className="sr-only">
                Role
              </label>
              <SelectDropdown
                id="member-role-select"
                value={member.roleId ?? ''}
                onChange={(v) => handleRoleChange(v || null)}
                disabled={roleUpdating}
                compact
                ariaLabel="Role"
                options={[
                  { value: '', label: 'No role' },
                  ...roles.map((r) => ({ value: r.id, label: r.name })),
                ]}
              />
              {roleUpdating && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
            </div>
          </div>
        </div>
        <FeatureGate flag="ui.ai.chat">
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <AskVantiChip
              question={`What should I know about team member ${memberDisplayName}?`}
              label="Ask Vanti about this member"
            />
          </div>
        </FeatureGate>
        <hr className="border-dark-border mt-4 mb-0" />
      </div>

      <div className="border-b border-dark-border mb-6">
        <nav
          className="flex gap-1 -mb-px overflow-x-auto"
          role="tablist"
          aria-label="Member analytics sections"
          onKeyDown={onMemberTabListKeyDown}
        >
          {memberTabs.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`member-tab-${id}`}
              aria-selected={activeMemberTab === id}
              aria-controls={`member-tabpanel-${id}`}
              tabIndex={activeMemberTab === id ? 0 : -1}
              onClick={() => setActiveMemberTab(id)}
              className={animatedTabTriggerClass(activeMemberTab === id)}
            >
              <AnimatedTabIndicator
                layoutId="member-detail-tab-indicator"
                isActive={activeMemberTab === id}
              />
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {activeMemberTab === 'activity' && (
        <div role="tabpanel" id="member-tabpanel-activity" aria-labelledby="member-tab-activity">
          <div className="section-block-mdl">
            <CommitHeatmap
              scope="member"
              organisationId={orgId}
              memberId={memberId}
              title="Commit activity"
            />
          </div>
          <MemberTechStackSection
            techStacks={techStacks}
            techCategoriesOpen={techCategoriesOpen}
            toggleTechCategory={toggleTechCategory}
          />
        </div>
      )}

      {activeMemberTab === 'churn' && (
        <div role="tabpanel" id="member-tabpanel-churn" aria-labelledby="member-tab-churn">
          <MemberChurnCardColumn
            churnLoading={churnLoading}
            churnData={churnData}
            churnWindowDays={churnWindowDays}
            churnPeriod={churnPeriod}
            churnChartType={churnChartType}
            setChurnPeriod={setChurnPeriod}
            setChurnChartType={setChurnChartType}
          />
        </div>
      )}

      {activeMemberTab === 'assignments' && (
        <div
          role="tabpanel"
          id="member-tabpanel-assignments"
          aria-labelledby="member-tab-assignments"
        >
          <MemberStreamsProjectsTreeSection
            streams={streams}
            projects={projects}
            repositories={repositories}
            unassignedRepositories={unassignedRepositories}
            streamsExpanded={streamsExpanded}
            projectsExpanded={projectsExpanded}
            toggleStream={toggleStream}
            toggleProject={toggleProject}
          />
        </div>
      )}

      {activeMemberTab === 'skills' && skillsTabEnabled && (
        <div role="tabpanel" id="member-tabpanel-skills" aria-labelledby="member-tab-skills">
          <MemberSkillsSection orgId={orgId} memberId={memberId} isOwnProfile={isOwnProfile} />
        </div>
      )}
    </PageContainer>
  );
};

export default MemberDetail;
